import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, Paperclip, Mic } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FileAttachment } from "@/components/chat/FileAttachment";
import { AttachmentDisplay } from "@/components/chat/AttachmentDisplay";
import { AudioRecorder } from "@/components/chat/AudioRecorder";

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  mime_type: string;
}

interface Message {
  id: string;
  sender_id: string;
  message: string;
  is_from_client: boolean;
  created_at: string;
  sender?: {
    first_name?: string;
    last_name?: string;
  };
  chat_attachments?: Attachment[];
}

export function ClientChat() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    
    fetchMessages();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('client-support-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'client_support_messages',
          filter: `client_id=eq.${user.id}`
        },
        (payload) => {
          setMessages(prev => [...prev, payload.new as Message]);
          scrollToBottom();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchMessages = async () => {
    if (!user) return;
    
    try {
      const { data: messagesData, error } = await supabase
        .from('client_support_messages')
        .select('*')
        .eq('client_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch attachments separately
      if (messagesData && messagesData.length > 0) {
        const messageIds = messagesData.map(m => m.id);
        const { data: attachments } = await supabase
          .from('chat_attachments')
          .select('*')
          .in('message_id', messageIds);

        const messagesWithAttachments = messagesData.map(msg => ({
          ...msg,
          chat_attachments: attachments?.filter(att => att.message_id === msg.id) || []
        }));
        
        setMessages(messagesWithAttachments || []);
      } else {
        setMessages(messagesData || []);
      }
      
      setTimeout(scrollToBottom, 100);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const uploadAttachment = async (file: File, messageId: string) => {
    if (!user) return;

    const filePath = `${user.id}/${Date.now()}_${file.name}`;
    
    const { error: uploadError } = await supabase.storage
      .from('chat-attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { error: dbError } = await supabase
      .from('chat_attachments')
      .insert({
        message_id: messageId,
        file_name: file.name,
        file_path: filePath,
        file_type: file.type.split('/')[0],
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user.id
      });

    if (dbError) throw dbError;
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && attachedFiles.length === 0) || !user || sending) return;

    setSending(true);
    try {
      const { data: messageData, error: messageError } = await supabase
        .from('client_support_messages')
        .insert({
          client_id: user.id,
          sender_id: user.id,
          message: newMessage.trim() || '📎 Attachment',
          is_from_client: true,
          read_by_agency: false
        })
        .select()
        .single();

      if (messageError) throw messageError;

      // Upload attachments
      for (const file of attachedFiles) {
        await uploadAttachment(file, messageData.id);
      }

      setNewMessage("");
      setAttachedFiles([]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValid = file.size <= 20 * 1024 * 1024; // 20MB
      if (!isValid) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 20MB limit`,
          variant: "destructive"
        });
      }
      return isValid;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAudioRecording = async (audioBlob: Blob) => {
    if (!user || sending) return;

    setSending(true);
    try {
      const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
      
      const { data: messageData, error: messageError } = await supabase
        .from('client_support_messages')
        .insert({
          client_id: user.id,
          sender_id: user.id,
          message: '🎤 Voice message',
          is_from_client: true,
          read_by_agency: false
        })
        .select()
        .single();

      if (messageError) throw messageError;

      await uploadAttachment(audioFile, messageData.id);
      setShowAudioRecorder(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="glass-card flex flex-col h-[calc(100vh-200px)] max-h-[800px]">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-border/50">
        <h3 className="text-lg sm:text-xl font-semibold">Chat with Support</h3>
        <p className="text-xs sm:text-sm text-muted-foreground">We typically respond within a few hours</p>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 sm:p-6" ref={scrollRef}>
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              <p className="text-sm sm:text-base">No messages yet. Start a conversation!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 sm:gap-3 ${msg.is_from_client ? 'justify-end' : 'justify-start'}`}
              >
                {!msg.is_from_client && (
                  <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs sm:text-sm">SA</AvatarFallback>
                  </Avatar>
                )}
                <div className={`flex flex-col max-w-[85%] sm:max-w-[70%] ${msg.is_from_client ? 'items-end' : 'items-start'} gap-2`}>
                  <div
                    className={`rounded-2xl px-3 py-2 sm:px-4 sm:py-2.5 ${
                      msg.is_from_client
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm sm:text-base break-words">{msg.message}</p>
                  </div>
                  
                  {msg.chat_attachments && msg.chat_attachments.length > 0 && (
                    <div className="flex flex-col gap-2">
                      {msg.chat_attachments.map((attachment) => (
                        <AttachmentDisplay key={attachment.id} attachment={attachment} />
                      ))}
                    </div>
                  )}
                  
                  <span className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                {msg.is_from_client && (
                  <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                    <AvatarFallback className="bg-muted text-xs sm:text-sm">You</AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 sm:p-4 border-t border-border/50 space-y-3">
        {/* Attached Files Preview */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((file, index) => (
              <FileAttachment
                key={index}
                file={file}
                onRemove={() => removeFile(index)}
              />
            ))}
          </div>
        )}

        {/* Audio Recorder */}
        {showAudioRecorder && (
          <AudioRecorder
            onRecordingComplete={handleAudioRecording}
            onCancel={() => setShowAudioRecorder(false)}
          />
        )}

        {/* Message Input */}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || attachedFiles.length >= 5}
            variant="outline"
            size="icon"
            className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
          >
            <Paperclip className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          <Button
            onClick={() => setShowAudioRecorder(!showAudioRecorder)}
            disabled={sending}
            variant="outline"
            size="icon"
            className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
          >
            <Mic className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={sending}
            className="flex-1 text-sm sm:text-base touch-manipulation"
          />
          
          <Button
            onClick={sendMessage}
            disabled={(!newMessage.trim() && attachedFiles.length === 0) || sending}
            size="icon"
            className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
